"use client"

import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"

import { cn } from "@/lib/utils"
import { shouldSuppressAutoFocus } from "@/hooks/use-mobile"

const Popover = PopoverPrimitive.Root

const PopoverTrigger = PopoverPrimitive.Trigger

const PopoverAnchor = PopoverPrimitive.Anchor

const PopoverContent = React.forwardRef<
    React.ElementRef<typeof PopoverPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & { usePortal?: boolean }
>(({ className, align = "center", sideOffset = 4, usePortal = true, onWheel, onTouchMove, ...props }, ref) => {
    const content = (
        <PopoverPrimitive.Content
            ref={ref}
            align={align}
            sideOffset={sideOffset}
            // TECH-011 — Mobile/tactile : pas d'auto-focus à l'ouverture (les
            // combobox cmdk auto-focusent leur champ de recherche → clavier
            // virtuel qui masque le contenu). L'appelant peut surcharger.
            onOpenAutoFocus={(event) => {
                if (shouldSuppressAutoFocus()) event.preventDefault()
            }}
            /**
             * ⚠️ **DANS UNE MODALE, LA MOLETTE NE SCROLLE PAS CE POPOVER SANS CES
             * DEUX LIGNES.** Le symptôme ne ressemble pas à un bug de scroll : la
             * liste est là, on peut taper, la barre de défilement est visible — elle
             * ne bouge simplement jamais. Rien en console, aucun test rouge.
             *
             * Mécanisme : `DialogContent` (Radix) enveloppe son overlay dans
             * `RemoveScroll`, qui pose un écouteur `wheel`/`touchmove` NON PASSIF sur
             * `document` et appelle `preventDefault()` pour tout événement qui ne
             * naît ni dans le verrou ni dans un « shard » (ici la seule
             * `DialogContent`). Or ce contenu part dans un **portail sur
             * `document.body`** : il n'est descendant DOM ni de l'un ni de l'autre.
             * Sa molette est donc annulée — par une bibliothèque tierce, depuis un
             * fichier que personne ne relit en cherchant « pourquoi ça ne scrolle
             * pas ».
             *
             * React 18 délègue ses écouteurs sur le conteneur du portail
             * (`document.body`), soit UN CRAN SOUS `document` : couper la
             * propagation ici suffit à ce que l'écouteur de `RemoveScroll` ne voie
             * jamais l'événement. L'isolement de la modale reste entier — c'est le
             * `body { overflow: hidden }` de `RemoveScrollBar` qui l'assure, pas ce
             * `preventDefault` (mesuré : page derrière immobile, `scrollTop = 0`).
             *
             * ⚠️ Ne pas « corriger » en retirant le portail (`usePortal={false}`) :
             * mesuré aussi — la molette reste bloquée ET le panneau se fait rogner
             * par l'`overflow` de `DialogContent`, dont le `translate-x-[-50%]` en
             * fait le bloc conteneur des descendants `fixed`. Deux défauts pour un.
             *
             * Même geste que `ui/time-picker.tsx` (colonnes heures/minutes).
             */
            onWheel={(event) => {
                event.stopPropagation()
                onWheel?.(event)
            }}
            onTouchMove={(event) => {
                event.stopPropagation()
                onTouchMove?.(event)
            }}
            // `popover-animated` : l'ouverture et la fermeture s'animent dans
            // `index.css` (même mouvement que les menus déroulants). Les classes
            // `animate-in`, `zoom-in-95`, `slide-in-from-*` de la ligne suivante
            // sont INERTES ici — le plugin tailwindcss-animate n'est pas installé.
            // Validé par le PO le 2026-09-16 après essai (« l'effet est subtil ») :
            // DESIGN_SYSTEM §7.3.
            className={cn(
                "popover-animated z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
                className
            )}
            {...props}
        />
    )
    return usePortal ? <PopoverPrimitive.Portal>{content}</PopoverPrimitive.Portal> : content
})
PopoverContent.displayName = PopoverPrimitive.Content.displayName

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor }
